import React, { useState, useEffect, useMemo } from "react";

// Components
import { List, Avatar, Flex, message, Tag, Space } from "antd";
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { CustomButton, CustomModalWithoutButton, CustomParagraph, CustomInput } from "../../../components";
import StepsHeading from "./StepsHeading";
import ConnectsSensors from "../../Modals/ConnectsSensors";
import { useSpin } from '../../../components/SpinContext';
import LinkIcon from "../../../assets/images/link-icon.svg";
import { GetSensors, DeleteSensor } from "../../../api's/onboarding";
import useToken from '../../../common/helper/usetoken';
import { useNavigate } from "react-router-dom";
import { getProviderImagePath } from '../../../common/utils/imageUtils';
import { processSensorList } from "../../../common/utils/sensorUtils";
import ZLShield from '../../../assets/images/ZLshield.png';
import Arrows from '../../../assets/images/arrows.png';

const Sensors = ({ onNext, onBack, onSkip }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { token, removeToken } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [list, setList] = useState([]);
  const [sensor_id, setSensorsId] = useState(null);
  const [sensorData, setSensorData] = useState(null);

  const processedAndFilteredList = useMemo(() => {
    if (!list) return [];
    const processed = processSensorList(list);
    if (!searchQuery) {
        return processed;
    }
    return processed.filter(provider =>
        provider?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [list, searchQuery]);

  const handleSuccess = () => {
    fetchData();
    setModalOpen(false);
    setSensorsId(null);
    setSensorData(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    showSpin();
    GetSensors(token)
      .then((res) => {
        if (res) {
          const data = res?.data || null;
          if (data && data?.data?.is_success) {
            setList(data.data.data || []);
          } else {
            message.error(data?.data?.message || "Failed to fetch sensors.");
          }
        }
      })
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const handleButtonClick = (item) => {
    if (item && item.id && item.name) {
      setSensorData(item);
      setSensorsId(item.id);
      setModalOpen(true);
    } else {
        console.error("Invalid item passed to handleButtonClick", item);
    }
  };

  const unauthorize = (error) => {
    if (error && error?.status === 401) {
      removeToken();
      navigate("/login");
    }
  };

  const handleDelete = (item) => {
    if (item && item?.company_sensor?.id) {
        showSpin();
        DeleteSensor(item.company_sensor.id, token)
            .then((res) => {
                const data = res?.data || null;
                if (data && data?.data?.is_success) {
                    message.success(data.data.message);
                    fetchData();
                } else {
                    message.error(data?.data?.message || "Failed to delete sensor.");
                }
            })
            .catch(unauthorize)
            .finally(hideSpin);
    }
  };

  return (
    <>
      <div className="default-steps-content">
        <StepsHeading
          heading="Connect your sensors"
          text="Connect your sensors so we can help you monitor the conditions of your shipments."
        />

        <CustomInput
          width={"98%"}
          bgColor="#F3F3F3"
          border="none"
          placeholderColor="#5E5E5E"
          placeholder="Search for a sensor..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined style={{ color: "#5E5E5E" }} />}
        />

        <List
          className="custom-list-container"
          itemLayout="horizontal"
          dataSource={processedAndFilteredList}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar src={<img src={getProviderImagePath(item.name, 'SensorImages')} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "contain" }} />} />}
                title={
                  <Space style={{ display: "flex" }}>
                    <CustomParagraph fontSize={14} fontWeight={500} color="#000" text={item.name} marginBottom={0} />
                    {item.is_enabled && item.status === "Connected" && (
                      <Tag color="success" icon={<CheckOutlined style={{ color: "#0E8345" }} />}>Synced</Tag>
                    )}
                  </Space>
                }
              />
              {item.status !== "Connected" ? (
                <CustomButton
                  onClick={() => handleButtonClick(item)}
                  height="36px"
                  fontSize="14px"
                  text="Link account"
                  bgColor="#E6E6E6"
                  borderRadius="8px"
                  color="#000"
                  boxShadow="none"
                  iconPosition="left"
                  image={LinkIcon}
                  gap="10px"
                  fontWeight={500}
                />
              ) : (
                <CloseOutlined style={{ color: "#5E5E5E" }} onClick={() => handleDelete(item)} />
              )}
            </List.Item>
          )}
        />

        {modalOpen && sensorData && (
          <CustomModalWithoutButton
            modalOpen={modalOpen}
            setModalOpen={(value) => {
                setModalOpen(value);
                if (!value) setSensorData(null); // Clear data when closing
            }}
            modalTitle={`Connect to ${sensorData.name}`}
            modalText="By providing API access to ZoomLogi, you're enabling ZoomLogi to retrieve your sensor information."
            headerIcon={
              <Flex align="center" justify="center" gap={12}>
                <Avatar
                  src={<img src={getProviderImagePath(sensorData.name, 'SensorImages')} alt={sensorData.name} style={{ width: '100%', objectFit: 'contain' }} />}
                  size={52}
                  style={{ backgroundColor: 'transparent', boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)', borderRadius: '50%' }}
                />
                <img src={Arrows} alt="connects to" style={{ height: '40px' }} />
                <Avatar
                  size={52}
                  style={{ backgroundColor: 'transparent', boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)', borderRadius: '50%' }}
                >
                  <div style={{ padding: '6px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={ZLShield} alt="ZoomLogi" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                </Avatar>
              </Flex>
            }
          >
            <ConnectsSensors
              closeModal={handleSuccess}
              id={sensor_id}
              sensorData={sensorData}
              onSuccess={handleSuccess}
            />
          </CustomModalWithoutButton>
        )}
      </div>
    </>
  );
};

export default Sensors;
